import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";

import { Video, FileCheck, Send, Handshake } from "lucide-react";

const benefits = [
  {
    icon: Video,
    title: "Industry Professional Sessions",
    description: "Sessions led by industry professionals",
  },
  {
    icon: FileCheck,
    title: "Mock Interviews & Feedback",
    description: "Practical training (mock GDs, mock PIs, resume feedback)",
  },
  {
    icon: Send,
    title: "Proven Placement Success",
    description:
      "Proven track record (500+ students got internships/placements via our training)",
  },
  {
    icon: Handshake,
    title: "Flexible Learning Formats",
    description: "Flexible formats – Online & Offline",
  },
];

const WhyUsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section
      id="benefits"
      className="w-full py-12 sm:py-16 md:py-20 lg:py-24 relative overflow-hidden overflow-x-hidden mt-8 sm:mt-10 md:mt-12"
    >
      {/* Background with dots + gradient overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "radial-gradient(#404040 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />
      <div
        className="absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 20%, black 70%)",
        }}
      />

      {/* Content sits on top of background */}
      <div
        className=" mx-auto px-4 sm:px-6 md:px-8 lg:px-4 relative z-10"
        ref={ref}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-12 md:mb-14 lg:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-5 md:mb-6 px-4">
            <span className="bg-text-gradient bg-clip-text text-transparent">
              Why Choose Us
            </span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
            Bridge the gap between classroom and corporate success
          </p>
        </motion.div>

        {/* Responsive Grid: 1 column on mobile, 2 columns on md and up */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6 max-w-7xl mx-auto">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{
                  scale: 1.05,
                  transition: { duration: 0.2 },
                }}
                className="group w-full"
              >
                <div className="bg-glass-gradient backdrop-blur-glass border border-primaryTheme/20 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-7 lg:p-8 h-full hover:border-primaryTheme/40 transition-all duration-300 hover:shadow-glow">
                  <div className="flex items-start sm:items-center text-left gap-3 sm:gap-4">
                    <div className="p-2.5 sm:p-3 rounded-lg sm:rounded-xl bg-primaryTheme/10 group-hover:bg-primaryTheme/20 transition-colors shrink-0">
                      <Icon className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-primaryTheme" />
                    </div>
                    <p className="text-sm sm:text-base text-white text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Call-to-action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-12 sm:mt-14 md:mt-16"
        ></motion.div>
      </div>
    </section>
  );
};

export default WhyUsSection;
